import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import BoxplotZeile from './BoxplotZeile';

/**
 * Zahlenstatistik eines Parameters in Kurzform (#244).
 *
 * **Warum das im aufgeklappten Bereich die Werteliste ersetzt:** bei einer
 * stetigen Groesse beantwortet eine Liste der haeufigsten Einzelwerte die
 * Triage-Frage nicht. `% Hemoglobin A1c` zeigt dort `5.7, 5.8, 4.9, 5.4, 6.0` --
 * das sagt weniger als Median 5.7 und ein Bereich von 4.9 bis 6.4. Bei einem
 * kategorialen Parameter ist es umgekehrt: dort **ist** die Werteliste der
 * Inhalt (`18 G Infiltration Scale` -> `Grade 0`).
 *
 * Gemessen am 2026-08-31 ueber 100 Detailkarten von hospital1: 22 kategorial
 * ohne Median, 19 numerisch mit Median, 4 Zwitter (Zahlen als Text, mit Median).
 *
 * **Das Kriterium ist die vorhandene Zahlenstatistik, nicht das Aussehen der
 * Werte.** `ABP Alarm [Low]` traegt `90, 85, 80` als Text *und* hat einen
 * Median -- wer nach dem Aussehen entscheidet, greift genau dort daneben.
 *
 * **Eingeschraenkt, nicht unterdrueckt (#264):** traegt ein nennenswerter
 * Anteil der Werte keine erfasste Einheit, mischt Median & Co. Zeilen mit
 * bekannter Einheit mit Zeilen, deren Einheit niemand kennt -- bei `Admit Wt`
 * ['kg', null] koennte darunter Pfund stecken, ohne dass die Zahl es zeigt.
 * Die Statistik bleibt trotzdem stehen: sie ist nuetzlich, ihre Grundlage
 * gehoert nur dazu.
 */
const WertekennzahlenCard = ({ muster, eingeschraenkt = false, anteilOhneEinheit = null }) => {
  const { t } = useTranslation();

  if (!muster || muster.medianValue == null) {
    return null;
  }

  const zahl = (wert) => (Number.isFinite(Number(wert)) ? Number(wert) : null);
  const kurz = (wert) => {
    const n = zahl(wert);
    if (n == null) return null;
    // Ganze Zahlen ohne Nachkommastellen, sonst zwei -- ein Median von 88
    // soll nicht als 88.00 dastehen.
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, '');
  };

  const median = kurz(muster.medianValue);
  const q1 = kurz(muster.q1Value);
  const q3 = kurz(muster.q3Value);
  const min = kurz(muster.minValue);
  const max = kurz(muster.maxValue);

  const felder = [
    { label: t('dataCatalog.expanded.median'), wert: median, hervor: true },
    // Quartilsabstand und Spannweite nur, wenn beide Enden da sind: ein
    // halbes Intervall ist keine Aussage, sondern eine Andeutung.
    { label: t('dataCatalog.expanded.iqr'), wert: q1 != null && q3 != null ? `${q1} – ${q3}` : null },
    { label: t('dataCatalog.expanded.range'), wert: min != null && max != null ? `${min} – ${max}` : null },
    // Standardabweichung (#245): der Quartilsabstand beschreibt die mittleren
    // 50 %, die Standardabweichung die ganze Verteilung. Vor profile_bundle 1.3
    // nicht im Bundle -- dann fehlt sie hier still, statt eine Null zu zeigen.
    { label: t('dataCatalog.expanded.stdev'), wert: kurz(muster.stdevValue) },
  ].filter((f) => f.wert != null);

  const anteilKurz = kurz(anteilOhneEinheit);

  return (
    <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, boxShadow: 1 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
        {t('dataCatalog.expanded.valueStatistics')}
      </Typography>

      {/* Eingeschraenkt statt unterdrueckt (#264): die Zahl bleibt, ihre
          Grundlage steht dazu. */}
      {eingeschraenkt && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
          <InfoIcon color="warning" fontSize="small" sx={{ mt: 0.1 }} />
          <Typography variant="caption" color="text.secondary">
            {t('dataCatalog.expanded.valueStatisticsRestricted', { percentage: anteilKurz })}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {felder.map((f) => (
          <Box key={f.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              {f.label}
            </Typography>
            <Chip
              label={f.wert}
              size="small"
              color={f.hervor ? 'primary' : 'default'}
              sx={{ fontWeight: 600, height: 20, fontSize: '0.72rem', fontFamily: 'monospace' }}
            />
          </Box>
        ))}
      </Box>

      {/* Der Boxplot ist die Form zu denselben Zahlen -- und zugleich der
          Ausreisser-Hinweis (#252): Whisker an den Tukey-Zaeunen, min/max als
          Punkte dahinter, eingefaerbt, wenn sie draussen liegen. */}
      <BoxplotZeile muster={muster} />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {t('dataCatalog.expanded.valueStatisticsHint')}
      </Typography>
    </Box>
  );
};

export default WertekennzahlenCard;
